/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui/core components
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import withStyles from '@material-ui/core/styles/withStyles';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import validationFormsStyle from 'assets/jss/material-dashboard-pro-react/views/validationFormsStyle';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// global variables
import { exchangeRateCurrencies } from 'config';
// our components
import { EXCHANGE_RATE } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import Loading from 'components/common/Loading';
import Datepicker from 'components/common/DatePicker';
import moment from 'moment';
import TextField from '@material-ui/core/TextField';

/**
 * @description
 * Validation form's exchange rate.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */
const translateValidationSchema = t =>
  Yup.object({
    currencyFrom: Yup.string().required(t('errors.required')),
    currencyTo: Yup.string().required(t('errors.required')),
    startDate: Yup.date().required(t('errors.required')),
    endDate: Yup.date().required(t('errors.required')),
    rate: Yup.number().required(t('errors.required')),
  });

/**
 * @class
 * @extends React.Component
 */
class ExchangeRateForm extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);

    const { id, rate, currencyFrom, currencyTo, startDate, endDate } = props;

    this.state = {
      fields: {
        id,
        rate,
        currencyFrom,
        currencyTo,
        startDate,
        endDate,
      },
    };

    this.formikRef = null;
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    history.push(EXCHANGE_RATE.LIST);
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Render Ad form.
   * @returns {*} - No returns value.
   */
  render() {
    const {
      classes,
      errors: serverErrors,
      editMode,
      submitLoading,
      exchangeRateSave,
      closeNotification,
      t,
    } = this.props;
    const { fields } = this.state;

    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);
    return (
      <React.Fragment>
        <Snackbars
          place="tc"
          message={all || ''}
          open={!!all}
          color="danger"
          close
          closeNotification={closeNotification}
        />
        <Formik
          ref={ref => {
            this.formikRef = ref;
          }}
          initialValues={fields}
          validationSchema={validationSchema}
          onSubmit={values =>
            exchangeRateSave({
              ...values,
            })
          }
          render={({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
          }) => (
            <GridItem className={classes.cardContainer}>
              <div
                className={`${classes.row} ${classes.contentBtns}`}
                style={{ justifyContent: 'space-between', display: 'flex' }}
              >
                <h3>
                  {editMode
                    ? t('exchange_rate.form.edit')
                    : t('exchange_rate.form.create')}
                </h3>
              </div>
              <form onSubmit={handleSubmit}>
                <Card>
                  <Loading show={submitLoading} />
                  <CardBody>
                    <GridContainer direction="column" alignItems="center">
                      <GridItem xs={12} sm={10}>
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <InputLabel
                            htmlFor="name"
                            className={classes.selectLabel}
                          >
                            {t('exchange_rate.form.fields.currencyFrom')}
                          </InputLabel>
                          <Select
                            style={{ marginLeft: '20px' }}
                            value={values.currencyFrom}
                            onChange={e => handleChange(e)}
                            MenuProps={{ className: classes.selectMenu }}
                            inputProps={{
                              name: 'currencyFrom',
                              id: 'currencyFrom',
                            }}
                          >
                            {Object.keys(exchangeRateCurrencies).map(
                              currency => (
                                <MenuItem
                                  key={exchangeRateCurrencies[currency]}
                                  classes={{
                                    root: classes.selectMenuItem,
                                    selected: classes.selectMenuItemSelected,
                                  }}
                                  value={exchangeRateCurrencies[currency]}
                                >
                                  {exchangeRateCurrencies[currency]}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <InputLabel
                            htmlFor="name"
                            className={classes.selectLabel}
                          >
                            {t('exchange_rate.form.fields.currencyTo')}
                          </InputLabel>
                          <Select
                            style={{ marginLeft: '20px' }}
                            value={values.currencyTo}
                            onChange={e => handleChange(e)}
                            MenuProps={{ className: classes.selectMenu }}
                            inputProps={{
                              name: 'currencyTo',
                              id: 'currencyTo',
                            }}
                          >
                            {Object.keys(exchangeRateCurrencies).map(
                              currency => (
                                <MenuItem
                                  key={exchangeRateCurrencies[currency]}
                                  classes={{
                                    root: classes.selectMenuItem,
                                    selected: classes.selectMenuItemSelected,
                                  }}
                                  value={exchangeRateCurrencies[currency]}
                                >
                                  {exchangeRateCurrencies[currency]}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                        <FormControl
                          fullWidth
                          className={classes.selectFormControl}
                        >
                          <TextField
                            id="rate"
                            label={t('exchange_rate.form.fields.rate')}
                            margin="normal"
                            helperText={touched.rate ? errors.rate : ''}
                            error={touched.rate && Boolean(errors.rate)}
                            InputProps={{
                              value: values.rate,
                              onChange: e => handleChange(e),
                              onBlur: handleBlur,
                              type: 'number',
                            }}
                          />
                        </FormControl>
                        <GridContainer>
                          <GridItem xs={12} sm={12} md={6}>
                            <Datepicker
                              title={t('exchange_rate.form.fields.startDate')}
                              defaultValue={t(
                                'exchange_rate.form.fields.data.from_placeholder',
                              )}
                              onChange={date => {
                                const startDate = moment(date).format(
                                  'YYYY-MM-DD',
                                );
                                if (
                                  values.endDate &&
                                  startDate &&
                                  values.endDate < startDate
                                ) {
                                  return;
                                }
                                handleChange({
                                  target: {
                                    name: 'startDate',
                                    id: 'startDate',
                                    value: startDate,
                                  },
                                });
                              }}
                              value={values.startDate}
                            />
                          </GridItem>
                          <GridItem xs={12} sm={12} md={6}>
                            <Datepicker
                              title={t('exchange_rate.form.fields.endDate')}
                              defaultValue={t(
                                'exchange_rate.form.fields.data.to_placeholder',
                              )}
                              onChange={date => {
                                const endDate = moment(date).format(
                                  'YYYY-MM-DD',
                                );
                                if (
                                  values.startDate &&
                                  endDate &&
                                  endDate < values.startDate
                                ) {
                                  return;
                                }
                                handleChange({
                                  target: {
                                    name: 'endDate',
                                    id: 'endDate',
                                    value: endDate,
                                  },
                                });
                              }}
                              value={values.endDate}
                            />
                          </GridItem>
                        </GridContainer>
                        <div className={classes.section}>
                          <GridContainer direction="row-reverse">
                            <GridItem xs={12} sm={6}>
                              <div className={classes.contentBtns}>
                                <Button
                                  id="save-exchange-rate"
                                  color="primary"
                                  type="submit"
                                  style={{ float: 'right' }}
                                >
                                  {editMode
                                    ? t('exchange_rate.form.edit_btn')
                                    : t('exchange_rate.form.create_btn')}
                                </Button>
                              </div>
                            </GridItem>
                            <GridItem xs={12} sm={6}>
                              <Button
                                id="cancel-create-exchange-rate"
                                simple
                                color="danger"
                                onClick={this.handleCancelBtn}
                                style={{ float: 'left' }}
                              >
                                {t('common.cancel')}
                              </Button>
                            </GridItem>
                          </GridContainer>
                        </div>
                      </GridItem>
                    </GridContainer>
                  </CardBody>
                </Card>
              </form>
            </GridItem>
          )}
        />
      </React.Fragment>
    );
  }
}

/**
 * ExchangeRateForm PropTypes
 * @param classes - styles received from withStyles
 * @param rate - Rate
 * @param currencyFrom - Original currency
 * @param currencyTo - Target currency
 * @param startDate - Start date
 * @param endDate - End date
 * @param errors - Errors data list
 * @param editMode - To active editing mode
 * @param submitLoading - To change to loading status
 * @param t - prop from HOC with string translations
 * @callback exchangeRateSave - callback called when user clicks save button
 * @callback closeNotification - callback to close notification
 */

ExchangeRateForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  id: PropTypes.string,
  rate: PropTypes.number,
  currencyFrom: PropTypes.string,
  currencyTo: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  errors: PropTypes.instanceOf(Array).isRequired,
  submitLoading: PropTypes.bool.isRequired,
  exchangeRateSave: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  closeNotification: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

ExchangeRateForm.defaultProps = {
  editMode: false,
  id: undefined,
  rate: 1,
  currencyFrom: exchangeRateCurrencies.USD,
  currencyTo: exchangeRateCurrencies.CUP,
  startDate: moment().format('YYYY-MM-DD'),
  endDate: moment()
    .add(1, 'month')
    .format('YYYY-MM-DD'),
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const ExchangeRateFormWrapper = props => <ExchangeRateForm {...props} />;

export default withTranslation()(
  withStyles(validationFormsStyle)(ExchangeRateFormWrapper),
);
